<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";
import { mapState } from "vuex";

export default {
  components: {
    Layout,
  },

  computed: {
    ...mapState({ account: (state) => state.account }),

    isRegional() {
      return this.account?.user?.package === "Regional";
    },
  },

  data() {
    return {
      loading: true,
      errored: false,

      indicated: null,
      documents: [],
    };
  },

  methods: {
    getIndicationDocuments() {
      this.loading = true;
      this.errored = false;

      api
        .get(`network/indication/documents/${this.$route.params.id}`)
        .then((response) => {
          if (response.data.status === "success") {
            this.indicated = response.data.indicated;
            this.documents = response.data.documents;
          } else {
            this.indicated = null;
            this.documents = [];
          }
        })
        .catch(() => {
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    approveIndicated() {
      this.$bvModal
        .msgBoxConfirm("Deseja realmente aprovar este indicado?", {
          title: "Aprovar Indicado",
          okVariant: "success",
          okTitle: "Aprovar",
          cancelTitle: "Cancelar",
          centered: true,
        })
        .then((confirmed) => {
          if (!confirmed) return;

          api
            .post("network/approveIndication", {
              id: this.indicated.id,
            })
            .then(() => {
              this.$toast.success("Indicado aprovado com sucesso.");
              this.$router.push("/network/indication");
            });
        });
    },

    reproveIndicated() {
      this.$bvModal
        .msgBoxConfirm("Deseja realmente reprovar este indicado?", {
          title: "Reprovar Indicado",
          okVariant: "danger",
          okTitle: "Reprovar",
          cancelTitle: "Cancelar",
          centered: true,
        })
        .then((confirmed) => {
          if (!confirmed) return;

          api
            .post("network/reproveIndication", {
              id: this.indicated.id,
            })
            .then(() => {
              this.$toast.success("Indicado reprovado.");
              this.$router.push("/network/indication");
            });
        });
    },
  },

  mounted() {
    this.getIndicationDocuments();
  },
};
</script>

<template>
  <Layout>
    <!-- TÍTULO + VOLTAR -->
    <div class="page-title-box d-flex justify-content-between align-items-center mb-4">
      <h3>Documentos do Indicado</h3>

      <button
        class="btn btn-outline-secondary"
        @click="$router.push('/network/indicados')"
      >
        ← voltar
      </button>
    </div>

    <!-- INFORMAÇÕES DO INDICADO -->
    <div v-if="indicated" class="card mb-4">
      <div class="card-header font-weight-bold">
        Informações do Indicado
      </div>

      <div class="card-body">
        <div class="row align-items-center">
          <!-- Avatar -->
          <div class="col-auto mb-3 mb-md-0">
            <img
              v-if="indicated.avatar"
              :src="indicated.avatar"
              class="rounded-circle"
              style="width: 64px; height: 64px"
            />
            <img
              v-else
              :src="`https://ui-avatars.com/api/?background=27416E&color=fff&name=${indicated.name}`"
              class="rounded-circle"
              style="width: 64px; height: 64px"
            />
          </div>

          <!-- Nome / Tipo / Data -->
          <div class="col">
            <h5 class="mb-1">{{ indicated.name }}</h5>
            <div class="text-muted small">
              {{ indicated.type }} • {{ indicated.date }}
            </div>
            <div class="text-muted small">
              Indicado por: <strong>{{ indicated.sponsor }}</strong>
            </div>

            <!-- STATUS MOBILE -->
            <div class="d-md-none mt-2">
              <span
                v-if="indicated.status === 'waiting_docs'"
                class="badge badge-soft-warning"
              >
                Aguardando Documentação
              </span>
              <span
                v-else-if="indicated.status === 'reproved'"
                class="badge badge-soft-danger"
              >
                Reprovado
              </span>
              <span
                v-else
                class="badge badge-soft-info"
              >
                Aguardando Aprovação
              </span>
            </div>
          </div>

          <!-- STATUS DESKTOP -->
          <div class="col-auto d-none d-md-block">
            <span
              v-if="indicated.status === 'waiting_docs'"
              class="badge badge-soft-warning p-2 text-uppercase"
            >
              Aguardando Documentação
            </span>
            <span
              v-else-if="indicated.status === 'reproved'"
              class="badge badge-soft-danger p-2 text-uppercase"
            >
              Reprovado
            </span>
            <span
              v-else
              class="badge badge-soft-info p-2 text-uppercase"
            >
              Aguardando Aprovação
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- DOCUMENTOS -->
    <div class="card">
      <div class="card-header font-weight-bold">
        Documentos
      </div>

      <div class="card-body">
        <div v-if="errored" class="text-center">
          Não foi possível carregar os documentos.
        </div>

        <div v-else-if="loading" class="text-center">
          Carregando...
        </div>

        <div v-else-if="documents.length === 0" class="text-center">
          Nenhum documento encontrado.
        </div>

        <div
          v-else
          class="row justify-content-center"
          v-for="(document, index) in documents"
          :key="index"
        >
          <div class="col-md-10 col-lg-8">
            <div class="card mb-3">
              <div
                class="card-body d-flex justify-content-between align-items-center flex-wrap"
              >
                <h6 class="mb-2 mb-md-0">
                  {{ document.name }}
                </h6>

                <div class="d-flex align-items-center">
                  <span
                    v-if="document.status === 'pending'"
                    class="badge badge-warning mr-2"
                  >
                    pending
                  </span>

                  <span
                    v-else-if="document.status === 'approved'"
                    class="badge badge-success mr-2"
                  >
                    aprovado
                  </span>

                  <span
                    v-else
                    class="badge badge-secondary mr-2"
                  >
                    não enviado
                  </span>

                  <a
                    v-if="document.url"
                    class="btn btn-primary btn-sm"
                    target="_blank"
                    :href="document.url"
                  >
                    ver
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- BOTÕES APROVAR / REPROVAR -->
        <div
          v-if="indicated && indicated.status === 'waiting_approval' && isRegional"
          class="text-center mt-4"
        >
          <button
            class="btn btn-success btn-lg px-5"
            @click="approveIndicated"
          >
            Aprovar Indicado
          </button>

          <button
            class="btn btn-danger btn-lg px-5 ml-2"
            @click="reproveIndicated"
          >
            Reprovar Indicado
          </button>
        </div>
      </div>
    </div>
  </Layout>
</template>
